import { motion } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { useInView } from '../../hooks/useAnimations';
import { useState } from 'react';

const testimonials = [
  { name: 'Priya Sharma', role: 'Sales Director', company: 'TechStart Solutions', content: 'TeleValley transformed our sales process. We increased conversions by 45% in just 3 months. The lead allocation feature is a game-changer.', rating: 5, avatar: 'PS' },
  { name: 'Rahul Mehta', role: 'Operations Manager', company: 'FinanceFirst Corp', content: 'The real-time analytics and call tracking features have given us unprecedented visibility into our team performance.', rating: 5, avatar: 'RM' },
  { name: 'Anjali Patel', role: 'CEO', company: 'GrowthHub', content: 'Best CRM for telecalling teams. The mobile app keeps our remote team connected and productive around the clock.', rating: 5, avatar: 'AP' },
  { name: 'Vikram Singh', role: 'Head of Sales', company: 'ScaleUp Inc', content: 'We switched from 3 different tools to TeleValley. Everything we need in one place. ROI was positive within the first month.', rating: 5, avatar: 'VS' },
  { name: 'Neha Gupta', role: 'CTO', company: 'InnovateTech', content: 'The API is well-documented and the support team is incredibly responsive. Integration was smooth and painless.', rating: 5, avatar: 'NG' },
  { name: 'Amit Kumar', role: 'Sales Manager', company: 'SalesPro', content: 'The automated follow-ups alone saved us 10+ hours per week. Our team can focus on closing deals instead of manual tasks.', rating: 5, avatar: 'AK' },
];

const Testimonials = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" ref={ref} className="py-24 bg-gradient-to-b from-white via-purple-50/30 to-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/4 left-1/4 w-[400px] h-[400px] rounded-full orb-purple"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] rounded-full orb-indigo"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 mb-6"
          >
            <Quote className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-700">Customer Love</span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
            What Our <span className="text-gradient">Customers Say</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            See how businesses like yours are scaling with TeleValley.
          </p>
        </motion.div>

        {/* Featured Testimonial - Desktop */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2 }}
          className="hidden md:block relative"
        >
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-purple-100 shadow-xl shadow-purple-500/5">
            {/* Quote Icon */}
            <div className="absolute top-6 left-6 w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
              <Quote className="w-6 h-6 text-purple-600" />
            </div>

            {/* Stars */}
            <div className="flex gap-1 mb-6 justify-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
              ))}
            </div>

            {/* Content */}
            <p className="text-xl md:text-2xl text-slate-700 text-center leading-relaxed mb-8 max-w-3xl mx-auto">
              "{testimonials[currentIndex].content}"
            </p>

            {/* Author */}
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-semibold text-lg mb-4">
                {testimonials[currentIndex].avatar}
              </div>
              <h4 className="text-lg font-semibold text-slate-900">{testimonials[currentIndex].name}</h4>
              <p className="text-slate-600">{testimonials[currentIndex].role} at {testimonials[currentIndex].company}</p>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="w-12 h-12 rounded-xl bg-slate-50 border border-purple-100 flex items-center justify-center text-slate-600 hover:text-purple-600 hover:bg-purple-50 transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      currentIndex === index ? 'bg-purple-600 w-6' : 'bg-purple-200 hover:bg-purple-300'
                    }`}
                  />
                ))}
              </div>
              <button
                onClick={nextTestimonial}
                className="w-12 h-12 rounded-xl bg-slate-50 border border-purple-100 flex items-center justify-center text-slate-600 hover:text-purple-600 hover:bg-purple-50 transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Testimonials Grid - Mobile */}
        <div className="md:hidden grid gap-4">
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <motion.div
              key={testimonial.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 + index * 0.1 }}
              className="group"
            >
              <div className="bg-white rounded-2xl p-6 border border-purple-100 shadow-lg shadow-purple-500/5">
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>

                {/* Content */}
                <p className="text-slate-600 leading-relaxed mb-4">"{testimonial.content}"</p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center text-white font-medium text-sm">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role} at {testimonial.company}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;